import type { ISnippetRule } from '@carboncopyco/types';
import { Badge, TextMenuHeader } from '@carboncopyco/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RuleCardToggle,
  RulesContent,
} from '../../RulesManager';
import { CustomCodeCreationForm } from './CustomCodeCreationForm';
import { CustomCodeSnippetPreview } from './CustomCodeSnippetPreview';

export interface CustomCodeListItemProps {
  snippets: ISnippetRule[];
  snippet: ISnippetRule;
  open?: boolean;
  onOpenRule: () => void;
  onCloseRule: () => void;
  onDelete: (snippet: ISnippetRule) => void;
  onUpdate: (snippet: ISnippetRule) => Promise<void>;
}

export const CustomCodeListItem = ({
  snippets,
  snippet,
  open,
  onOpenRule,
  onCloseRule,
  onDelete,
  onUpdate,
}: CustomCodeListItemProps) => {
  const [autoFocus, setAutoFocus] = useState(false);
  const prevRules = useRef<ISnippetRule[]>(snippets);

  useEffect(() => {
    if (prevRules.current?.length !== snippets.length) {
      onCloseRule();
      prevRules.current = snippets;
    }
  }, [snippets, onCloseRule]);

  const dropdownItems = useMemo(
    () =>
      [
        {
          type: 'edit',
          disabled: !snippet.isActive,
          onClick: () => {
            onOpenRule();
            setAutoFocus(true);
          },
        },
        {
          type: 'delete',
          onClick: () => onDelete(snippet),
        },
      ] as RuleCardDropdownProps['items'],
    [snippet, setAutoFocus, onOpenRule, onDelete]
  );

  const disabled = !snippet.isActive;

  useEffect(() => {
    if (!open) {
      setAutoFocus(false);
    }
  }, [open, setAutoFocus]);

  return (
    <RulesContent.List.Item
      id={snippet.ruleId}
      header={
        open ? (
          <TextMenuHeader
            label="Snippet settings"
            color={disabled ? 'text-ds-natural-200' : 'text-ds-natural-600'}
          />
        ) : (
          <CustomCodeSnippetPreview
            snippet={snippet}
            disabled={disabled}
          />
        )
      }
      content={
        <CustomCodeCreationForm
          snippets={snippets}
          snippet={snippet}
          type={snippet.snippetType}
          autoFocus={autoFocus}
          onFocus={() => setAutoFocus(false)}
          onSubmit={async (snippet) => {
            await onUpdate(snippet);
            // a small delay before the closing animation
            setTimeout(onCloseRule, 300);
          }}
          onCancel={onCloseRule}
          disabled={disabled}
        />
      }
      action={
        <>
          <RuleCardToggle
            cardOpen={open}
            checked={snippet.isActive}
            onChange={() => {
              onUpdate({
                ...snippet,
                isActive: !snippet.isActive,
              });
            }}
          />
          <RuleCardDropdown items={dropdownItems} />
        </>
      }
      iconRight={
        !snippet.isActive &&
        !open && (
          <div className="group-hover:hidden block">
            <Badge
              label="off"
              variant="disabled"
            />
          </div>
        )
      }
    />
  );
};
